"use client";

import axios from "axios";
import { useRouter, useSearchParams } from "next/navigation";
import React, { useEffect, useState } from "react";
import PageLoading from "../page-loading";
import { logout } from "@/services";
import { useToast } from "@/components/ui";
import { AuthorizeResponse } from "@/types";
import { useAuthContext } from "@/components/providers/auth-provider";

function AuthWrapper({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { toast } = useToast();
  const { setAllowedScreens, setUsername, setEmail, setUserRole } = useAuthContext();

  const code = searchParams.get("code");
  const state = searchParams.get("state") || localStorage.getItem("oauth_state") || "";

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkSession = async () => {
      try {
        const resSession = await fetch("/api/auth/session", {
          credentials: "include",
        });

        const dataSession = await resSession.json();

        if (dataSession.authenticated) {
          const resUserInfo = await fetch("/api/oauth/user-info");
          const dataUserInfo = await resUserInfo.json();
          const { email, name } = dataUserInfo.data;
          setEmail(email);
          setUsername(name);

          await axios
            .post("/api/auth/authorize", {})
            .then(resAuthorize => {
              const {
                data: { allowed_screens },
              } = resAuthorize.data as AuthorizeResponse;

              if (allowed_screens && allowed_screens.length > 0) {
                setAllowedScreens(allowed_screens);
                setUserRole(
                  allowed_screens.includes("/pending-approval")
                    ? "Security Lead"
                    : "Security Specialist"
                );
                return;
              }

              router.replace("/unauthorized");
            })
            .catch(error => {
              throw new Error(error);
            });
          return;
        }

        if (code && state) {
          const codeVerifier = localStorage.getItem("code_verifier");

          if (!codeVerifier) {
            logout({ state }).then(() => {
              document.cookie = "access_token=; id_token=; path=/; max-age=0";
              localStorage.clear();
            });
            toast({
              variant: "error",
              description: "Failed to authenticate. Please login again.",
              duration: 2000,
            });
            router.replace("/unauthorized");
            return;
          }

          const resUserInfo = await fetch("/api/oauth/user-info");
          const dataUserInfo = await resUserInfo.json();
          const { email, name } = dataUserInfo.data;
          setEmail(email);
          setUsername(name);

          await axios
            .post("/api/auth/authorize", {})
            .then(resAuthorize => {
              const {
                data: { allowed_screens },
              } = resAuthorize.data as AuthorizeResponse;

              if (allowed_screens && allowed_screens.length > 0) {
                setAllowedScreens(allowed_screens);
                router.replace(allowed_screens[0].toLowerCase());
                return;
              }

              router.replace("/unauthorized");
            })
            .catch(error => {
              throw new Error(error);
            });

          return;
        }

        if (!dataSession.authenticated) {
          logout({ state }).then(() => {
            document.cookie = "access_token=; id_token=; path=/; max-age=0";
            localStorage.clear();
          });
          router.replace("/unauthorized");
          return;
        }
      } catch (error) {
        logout({ state }).then(() => {
          document.cookie = "access_token=; id_token=; path=/; max-age=0";
          localStorage.clear();
        });
        router.replace("/unauthorized");
      } finally {
        setLoading(false);
      }
    };

    checkSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router]);

  if (loading) {
    return <PageLoading />;
  }

  return <>{children}</>;
}

export default AuthWrapper;
